import type { TFunction } from 'i18next';
import type { SubscriptionBlockViewDto } from '@subscriptions/application/dtos';
import { formatCurrencyMXN } from '@subscriptions/application/utils';
import { SubscriptionStatuses, SubscriptionBlockSubStatuses } from '@subscriptions/domain/types/SubscriptionsConst';
import dayjs from 'dayjs';
import 'dayjs/locale/es';

export { formatCurrencyMXN };

/** Surface where the license badge is rendered. */
export type LicenseBadgeSurface = 'card' | 'detail';

/** License payment badge to render, or null when no license badge applies. */
export type LicenseBadgeVariant = 'paid' | 'pending' | null;

/**
 * Resolves which license payment badge a subscription block shows.
 *
 * `PAID` is the only state that differs between surfaces: the card marks it with the paid
 * badge, while the detail leaves the header without one because its title already carries
 * the charge date.
 */
export const getLicenseBadgeVariant = (
  block: Pick<SubscriptionBlockViewDto, 'status' | 'subStatus'>,
  surface: LicenseBadgeSurface,
): LicenseBadgeVariant => {
  const isMissingPayment =
    block.status === SubscriptionStatuses.UNAVAILABLE || block.status === SubscriptionStatuses.PAYMENT_REQUIRED;

  if (isMissingPayment) return null;

  switch (block.subStatus) {
    case SubscriptionBlockSubStatuses.PRE_PAID:
      return 'paid';
    case SubscriptionBlockSubStatuses.PENDING:
      return 'pending';
    case SubscriptionBlockSubStatuses.PAID:
      return surface === 'card' ? 'paid' : null;
    default:
      return null;
  }
};

/**
 * Sums numeric concept values.
 *
 * @param concepts - Array of concept key-value pairs
 * @returns Numeric total
 */
export const sumConcepts = (concepts: { key: string; value: number }[]): number =>
  concepts.reduce((sum, concept) => sum + concept.value, 0);

/**
 * Whether a subscription block is on a promotional (discounted) plan.
 *
 * A block qualifies when the backend `discounts` node is present and its aggregated
 * `total` (sum of every store's recurring discount, in MXN) is greater than zero.
 * `expirationDate` is deliberately ignored: the backend only fills it for INDIVIDUAL
 * blocks, so it is not a reliable vigency signal for every block.
 *
 * @param block - Subscription block view data
 * @returns Whether the block shows the "Promoción" badge instead of "Mensual"
 */
export const isPromoBlock = (block: Pick<SubscriptionBlockViewDto, 'discounts'>): boolean =>
  Boolean(block.discounts) && block.discounts!.total > 0;

/** Separator between plan groups of the multi-store subtitle. */
const PLAN_GROUP_SEPARATOR = ' · ';

/**
 * Builds the card subtitle describing the plans of a block's stores.
 *
 * Groups are ordered by first appearance in `stores` — the backend does not expose a
 * plan tier, so no semantic ordering is possible today.
 *
 * @param stores - Stores of the subscription block
 * @param t - i18next translation function bound to the `subscriptions` namespace
 * @returns Localized subtitle, or an empty string when the block has no stores
 *
 * @example
 * getPlanSubtitle([{ plan: 'All In One', ... }], t)                        // "Plan All In One"
 * getPlanSubtitle([{ plan: 'Starter' }, { plan: 'Pro' }, { plan: 'Pro' }]) // "Starter (x1) · Pro (x2)"
 */
export const getPlanSubtitle = (stores: SubscriptionBlockViewDto['stores'], t: TFunction): string => {
  if (stores.length === 0) return '';
  if (stores.length === 1) return t('card.planSingle', { plan: stores[0].plan });

  const groups = new Map<string, number>();
  stores.forEach((store) => groups.set(store.plan, (groups.get(store.plan) ?? 0) + 1));

  return Array.from(groups.entries())
    .map(([plan, count]) => t('card.planGroupItem', { plan, count }))
    .join(PLAN_GROUP_SEPARATOR);
};

/**
 * Normalizes a string for accent- and case-insensitive comparison.
 * Lowercases and strips diacritics via NFD decomposition ("Cancún" → "cancun").
 *
 * @param value - Raw string to normalize
 * @returns Lowercased string without diacritics
 */
const normalizeForSearch = (value: string): string =>
  value
    .toLowerCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '');

/**
 * Checks whether a subscription block matches a search term.
 *
 * A block matches when its name OR at least one of its stores' names contains
 * the term. Comparison is case- and accent-insensitive ("Cancún" ≈ "cancun").
 * An empty or whitespace-only term matches every block (current behavior).
 *
 * @param block - Subscription block view data
 * @param term - Raw search term typed by the user
 * @returns Whether the block matches the search term
 */
export const matchesSearchTerm = (block: Pick<SubscriptionBlockViewDto, 'name' | 'stores'>, term: string): boolean => {
  const normalizedTerm = normalizeForSearch(term.trim());
  if (!normalizedTerm) return true;

  if (normalizeForSearch(block.name).includes(normalizedTerm)) return true;

  return block.stores.some((store) => normalizeForSearch(store.storeName).includes(normalizedTerm));
};

/**
 * Formats payment date as "DD de MMMM YYYY".
 * Empty or invalid dates (soft-validated reconstruction) render as "-".
 */
export const formatPaymentDate = (dateStr: string): string => {
  if (!dateStr?.trim()) return '-';
  const date = dayjs(dateStr);
  if (!date.isValid()) return '-';
  return date.locale('es').format('D [de] MMMM YYYY');
};
